import { makeFinalFormValidator } from '@readcloud/common';
import { Config, FormState } from 'final-form';
import React from 'react';
import { Form, FormRenderProps, useForm, useFormState } from 'react-final-form';
import { useDebouncedCallback } from 'use-debounce';
import * as yup from 'yup';
import * as commonElements from './CommonElements';
import * as commonSchemas from './CommonSchemas';

export function makeForm<Values extends object>(params: {
  schema: yup.ObjectSchema<Values>;
}) {
  const validator = makeFinalFormValidator(params.schema);

  function EditFormInner<Values>(
    props: FormRenderProps<Values> & {
      autoSubmit?: boolean;
      autoSubmitDebounceTime?: number;
      children:
        | React.ReactNode
        | ((params: Omit<typeof props, 'children'>) => React.ReactNode);
    }
  ) {
    const [debouncedSubmit] = useDebouncedCallback(() => {
      if (props.autoSubmit) props.handleSubmit();
    }, props.autoSubmitDebounceTime || 300);
    useFormState({
      subscription: { values: true },
      onChange: debouncedSubmit,
    });

    return (
      <Form onSubmit={props.handleSubmit}>
        {typeof props.children === 'function'
          ? props.children(props)
          : props.children}
      </Form>
    );
  }

  return function EditForm(
    props: {
      initialValues?: Values;
      onSubmit: Config['onSubmit'];
    } & Pick<
      React.ComponentProps<typeof EditFormInner>,
      'children' | 'autoSubmit' | 'autoSubmitDebounceTime'
    >
  ) {
    const render = React.useCallback(
      (renderProps: FormRenderProps<object | Values>) => {
        return (
          <EditFormInner<object | Values>
            {...renderProps}
            autoSubmitDebounceTime={props.autoSubmitDebounceTime}
            autoSubmit={props.autoSubmit}
            children={props.children as any}
          />
        );
      },
      [props.autoSubmit, props.children]
    );

    return (
      <Form
        render={render}
        initialValues={props.initialValues}
        // @ts-expect-error
        onSubmit={props.onSubmit}
        validate={validator}
      />
    );
  };
}

export function AutoSubmit(props: {
  enabled?: boolean;
  debounceTime?: number;
}) {
  const form = useForm();
  const [debouncedSubmit] = useDebouncedCallback(
    (formState: FormState<any>) => {
      if (props.enabled) {
        form.submit();
      }
    },
    props.debounceTime || 300
  );

  useFormState({
    subscription: { values: true },
    onChange: debouncedSubmit,
  });

  return null;
}

export { commonSchemas };
export { commonElements };
